/**
 * DASH2 Chart Theme - Dark Mode
 *
 * Centralized Chart.js styling for consistent, branded visualizations.
 * Updated for dark theme with glassmorphism effects.
 */

// =============================================================================
// BRAND COLORS - DARK THEME
// =============================================================================

export const chartColors = {
  // Primary brand palette (Powertron green)
  primary: {
    main: '#A0CE4E',
    light: '#C5E87D',
    dark: '#7CB342',
    darker: '#558B2F'
  },

  // Secondary palette (deep dark)
  secondary: {
    main: '#1A1A1A',
    light: '#2D2D2D',
    dark: '#0A0A0A'
  },

  // Efficiency scale (for performance-based coloring) - brighter for dark theme
  efficiency: {
    excellent: '#4ECDC4',  // Teal - top performers (>30%)
    good: '#A0CE4E',       // Powertron green - above average (20-30%)
    average: '#FFD93D',    // Yellow - baseline (15-20%)
    below: '#FF6B35',      // Orange - needs attention (10-15%)
    poor: '#E53E3E'        // Red - critical (<10%)
  },

  // Categorical colors for multi-series charts
  categorical: [
    '#A0CE4E',  // Powertron green
    '#FF6B35',  // Orange
    '#4ECDC4',  // Teal
    '#63B3ED',  // Blue
    '#9F7AEA',  // Purple
    '#F687B3',  // Pink
    '#FFD93D',  // Yellow
    '#FC8181'   // Coral
  ],

  // Semantic colors
  success: '#4ECDC4',
  warning: '#FFD93D',
  error: '#E53E3E',
  info: '#63B3ED',

  // Dark theme grid/text colors
  grid: 'rgba(255, 255, 255, 0.06)',
  gridDark: 'rgba(255, 255, 255, 0.1)',
  text: '#F7F7F7',
  textSecondary: '#A0A0A0',
  textMuted: '#666666',

  // Background for dark theme
  background: '#141414',
  backgroundElevated: '#1A1A1A'
}

// =============================================================================
// GRADIENT GENERATORS
// =============================================================================

/**
 * Creates a vertical gradient for bar charts
 */
export function createGradient(ctx, color1, color2, height = 400) {
  const gradient = ctx.createLinearGradient(0, 0, 0, height)
  gradient.addColorStop(0, color1)
  gradient.addColorStop(1, color2)
  return gradient
}

/**
 * Creates primary brand gradient
 */
export function createPrimaryGradient(ctx, height = 400) {
  return createGradient(ctx, chartColors.primary.main, chartColors.primary.dark, height)
}

/**
 * Creates a gradient from solid color to transparent (for area charts)
 */
export function createFadeGradient(ctx, color, height = 400) {
  const gradient = ctx.createLinearGradient(0, 0, 0, height)
  gradient.addColorStop(0, withOpacity(color, 0.6))
  gradient.addColorStop(1, withOpacity(color, 0.02))
  return gradient
}

/**
 * Get color with opacity
 */
export function withOpacity(color, opacity) {
  // Handle hex colors
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16)
    const g = parseInt(color.slice(3, 5), 16)
    const b = parseInt(color.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }
  return color
}

// =============================================================================
// EFFICIENCY COLOR HELPER
// =============================================================================

/**
 * Returns color based on efficiency percentage
 */
export function getEfficiencyColor(efficiency) {
  if (efficiency >= 30) return chartColors.efficiency.excellent
  if (efficiency >= 20) return chartColors.efficiency.good
  if (efficiency >= 15) return chartColors.efficiency.average
  if (efficiency >= 10) return chartColors.efficiency.below
  return chartColors.efficiency.poor
}

/**
 * Returns array of colors for efficiency values
 */
export function getEfficiencyColors(efficiencies) {
  return efficiencies.map(getEfficiencyColor)
}

// =============================================================================
// DEFAULT CHART OPTIONS - DARK THEME
// =============================================================================

export const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,

  // Animation
  animation: {
    duration: 800,
    easing: 'easeOutQuart'
  },

  // Interaction
  interaction: {
    intersect: false,
    mode: 'index'
  },

  // Layout
  layout: {
    padding: {
      top: 10,
      right: 10,
      bottom: 10,
      left: 10
    }
  },

  // Plugins
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        font: {
          family: '"Plus Jakarta Sans", sans-serif',
          size: 12,
          weight: 500
        },
        color: chartColors.text
      }
    },

    tooltip: {
      enabled: true,
      // Glassmorphism-style tooltip
      backgroundColor: 'rgba(20, 20, 20, 0.95)',
      titleColor: '#F7F7F7',
      bodyColor: '#A0A0A0',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 14,
      titleFont: {
        family: '"Outfit", sans-serif',
        size: 14,
        weight: 600
      },
      bodyFont: {
        family: '"JetBrains Mono", monospace',
        size: 12,
        weight: 500
      },
      footerFont: {
        family: '"Plus Jakarta Sans", sans-serif',
        size: 11
      },
      displayColors: true,
      boxPadding: 6,
      caretSize: 6,
      caretPadding: 8
    },

    datalabels: {
      display: false
    }
  },

  // Scales - Dark theme
  scales: {
    x: {
      grid: {
        display: false,
        drawBorder: false
      },
      border: {
        display: false
      },
      ticks: {
        font: {
          family: '"Plus Jakarta Sans", sans-serif',
          size: 11,
          weight: 500
        },
        color: chartColors.textSecondary,
        padding: 8
      }
    },
    y: {
      grid: {
        color: chartColors.grid,
        drawBorder: false,
        lineWidth: 1
      },
      border: {
        display: false
      },
      ticks: {
        font: {
          family: '"JetBrains Mono", monospace',
          size: 11,
          weight: 500
        },
        color: chartColors.textSecondary,
        padding: 8
      }
    }
  }
}

// =============================================================================
// BAR CHART DEFAULTS
// =============================================================================

export const barDefaults = {
  borderRadius: 4,
  borderWidth: 0,
  barPercentage: 0.75,
  categoryPercentage: 0.85,

  // Hover state
  hoverBackgroundColor: chartColors.primary.light,
  hoverBorderColor: chartColors.primary.main,
  hoverBorderWidth: 2
}

export const barOptions = {
  ...defaultOptions,
  plugins: {
    ...defaultOptions.plugins,
    legend: {
      display: false
    }
  }
}

// =============================================================================
// LINE CHART DEFAULTS
// =============================================================================

export const lineDefaults = {
  tension: 0.4,
  borderWidth: 3,
  pointRadius: 0,
  pointHoverRadius: 6,
  pointHoverBackgroundColor: chartColors.background,
  pointHoverBorderColor: chartColors.primary.main,
  pointHoverBorderWidth: 3,
  fill: true
}

export const lineOptions = {
  ...defaultOptions,
  elements: {
    line: lineDefaults
  }
}

// =============================================================================
// DOUGHNUT/PIE CHART DEFAULTS
// =============================================================================

export const doughnutDefaults = {
  cutout: '65%',
  borderWidth: 0,
  hoverBorderWidth: 3,
  hoverBorderColor: chartColors.background,
  hoverOffset: 8
}

export const doughnutOptions = {
  ...defaultOptions,
  plugins: {
    ...defaultOptions.plugins,
    legend: {
      ...defaultOptions.plugins.legend,
      position: 'right',
      labels: {
        ...defaultOptions.plugins.legend.labels,
        color: chartColors.text
      }
    }
  }
}

// =============================================================================
// SCATTER CHART DEFAULTS
// =============================================================================

export const scatterDefaults = {
  pointRadius: 6,
  pointHoverRadius: 10,
  pointBackgroundColor: withOpacity(chartColors.primary.main, 0.7),
  pointBorderColor: chartColors.primary.dark,
  pointBorderWidth: 2,
  pointHoverBackgroundColor: chartColors.primary.main,
  pointHoverBorderColor: chartColors.background,
  pointHoverBorderWidth: 3
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Merge options with defaults
 */
export function mergeOptions(customOptions, baseOptions = defaultOptions) {
  return {
    ...baseOptions,
    ...customOptions,
    plugins: {
      ...baseOptions.plugins,
      ...(customOptions.plugins || {}),
      tooltip: {
        ...baseOptions.plugins.tooltip,
        ...(customOptions.plugins?.tooltip || {})
      },
      legend: {
        ...baseOptions.plugins.legend,
        ...(customOptions.plugins?.legend || {})
      }
    },
    scales: {
      x: {
        ...baseOptions.scales.x,
        ...(customOptions.scales?.x || {})
      },
      y: {
        ...baseOptions.scales.y,
        ...(customOptions.scales?.y || {})
      }
    }
  }
}

/**
 * Create dataset with defaults
 */
export function createBarDataset(label, data, options = {}) {
  return {
    label,
    data,
    backgroundColor: chartColors.primary.main,
    borderColor: chartColors.primary.dark,
    ...barDefaults,
    ...options
  }
}

/**
 * Create line dataset with defaults
 */
export function createLineDataset(label, data, options = {}) {
  return {
    label,
    data,
    borderColor: chartColors.primary.main,
    backgroundColor: withOpacity(chartColors.primary.main, 0.15),
    ...lineDefaults,
    ...options
  }
}

/**
 * Format number for display
 */
export function formatNumber(value, decimals = 1) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(decimals)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(decimals)}K`
  return value.toFixed(decimals)
}

/**
 * Format currency
 */
export function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

/**
 * Format percentage
 */
export function formatPercent(value, decimals = 1) {
  return `${value.toFixed(decimals)}%`
}

// =============================================================================
// CHART.JS GLOBAL DEFAULTS - DARK THEME
// =============================================================================

/**
 * Apply to Chart.js instance to set global defaults
 * Call once in your app initialization
 */
export function applyGlobalChartDefaults(ChartJS) {
  // Font defaults
  ChartJS.defaults.font.family = '"Plus Jakarta Sans", sans-serif'
  ChartJS.defaults.font.size = 12

  // Color defaults - dark theme
  ChartJS.defaults.color = chartColors.textSecondary
  ChartJS.defaults.borderColor = chartColors.grid

  // Animation defaults
  ChartJS.defaults.animation.duration = 800
  ChartJS.defaults.animation.easing = 'easeOutQuart'

  // Element defaults
  ChartJS.defaults.elements.bar.borderRadius = 4
  ChartJS.defaults.elements.line.tension = 0.4
  ChartJS.defaults.elements.point.radius = 0

  // Plugin defaults - glassmorphism tooltip
  ChartJS.defaults.plugins.tooltip.backgroundColor = 'rgba(20, 20, 20, 0.95)'
  ChartJS.defaults.plugins.tooltip.borderColor = 'rgba(255, 255, 255, 0.1)'
  ChartJS.defaults.plugins.tooltip.borderWidth = 1
  ChartJS.defaults.plugins.tooltip.cornerRadius = 8
  ChartJS.defaults.plugins.tooltip.padding = 14
}

export default {
  colors: chartColors,
  options: defaultOptions,
  barOptions,
  lineOptions,
  doughnutOptions,
  barDefaults,
  lineDefaults,
  doughnutDefaults,
  scatterDefaults,
  createGradient,
  createPrimaryGradient,
  createFadeGradient,
  withOpacity,
  getEfficiencyColor,
  getEfficiencyColors,
  mergeOptions,
  createBarDataset,
  createLineDataset,
  formatNumber,
  formatCurrency,
  formatPercent,
  applyGlobalChartDefaults
}
